import React from 'react';
import { ShieldCheckIcon } from 'lucide-react';
import { usePageMeta } from '../../hooks/usePageMeta';
import { candidateResources } from '../../data/company';
import { faqCategories } from '../../data/faqs';
import { jobs } from '../../data/jobs';
import { images } from '../../data/images';
import { PageHero } from '../../components/ui/PageHero';
import { SectionHeading } from '../../components/ui/SectionHeading';
import { Button } from '../../components/ui/Button';
import { Accordion } from '../../components/ui/Accordion';
import { Reveal } from '../../components/ui/Reveal';
import { CareerPathway } from '../../components/visuals/CareerPathway';
import { JobCard } from '../../components/jobs/JobCard';
import { CandidateRegistrationForm } from '../../components/forms/CandidateRegistrationForm';
import { NewsletterForm } from '../../components/forms/NewsletterForm';
const dataPoints = ['Your details are never shared with an organisation without your explicit permission.', 'Only the consultants working with you can access your information.', 'You can ask us to update or delete your data at any time.', 'We retain information only as long as needed for recruitment purposes.'];
export function Candidates() {
  usePageMeta('Candidates & Career Support', 'Confidential career support, executive transitions, CV and interview guidance, and candidate registration with Corventa.');
  const latest = [...jobs].sort((a, b) => b.posted.localeCompare(a.posted)).slice(0, 3);
  return <>
      <PageHero crumbs={[{
      label: 'Candidates'
    }]} kicker="For candidates" title="Explore Your Next Opportunity." intro="Whether you are actively looking or simply open to the right conversation, we offer confidential, consultative support at every stage of your career." actions={<>
            <Button to="#register" size="lg" arrow data-id="element-597" data-mp="23mxnyd9cfdux">
              Register confidentially
            </Button>
            <Button to="/jobs" size="lg" variant="outlineLight" data-id="element-598" data-mp="f17d82qmydne">
              Browse opportunities
            </Button>
          </>} aside={<img src={images.candidate} alt="A professional reviewing notes beside a window" className="aspect-[4/5] max-h-[460px] w-full rounded-xl object-cover" data-id="element-599" data-mp="a3faedogd74f" />} data-id="element-596" data-mp="3tw7n6477cws9" />

      <section className="bg-ivory py-24" data-id="element-600" data-mp="ct1f2b8reup">
        <div className="container-page grid gap-12 lg:grid-cols-12 lg:items-center" data-id="element-601" data-mp="brmunv0oa88">
          <div className="lg:col-span-5" data-id="element-602" data-mp="3rjdo69u9k0lx">
            <SectionHeading kicker="Professional progression" title="Support shaped around where you are — and where you are heading." intro="Select a career level to see how we support professionals at each stage. We offer guidance and introductions, not guarantees: every outcome depends on the opportunity and the fit." data-id="element-603" data-mp="3rg9dquq7g8mt" />
          </div>
          <div className="lg:col-span-7" data-id="element-604" data-mp="1go6s897jsgxm">
            <CareerPathway data-id="element-605" data-mp="1br6lrx6sdc5z" />
          </div>
        </div>
      </section>

      <section id="resources" className="scroll-mt-24 bg-white py-24" data-id="element-606" data-mp="1jb8sy1w9wq11">
        <div className="container-page" data-id="element-607" data-mp="1j8wevztmyzt9">
          <SectionHeading kicker="Resources" title="Guidance for every stage of the process." data-id="element-608" data-mp="1o9k0g5z3iog3" />
          <div className="mt-12 grid gap-10 md:grid-cols-2" data-id="element-609" data-mp="1t08unt25tzix">
            {candidateResources.map((r, i) => <Reveal key={r.title} delay={i % 2 * 0.05} className="border-t border-line pt-6" data-id="element-610" data-mp="1y04z1terrsz0" data-mpi={i}>
                <h3 className="font-display text-3xl font-semibold text-ink" data-id="element-611" data-mp="10cwkyjh3lm71">{r.title}</h3>
                <p className="mt-3 text-lg leading-relaxed text-muted" data-id="element-612" data-mp="15aochcdtz6ap">{r.text}</p>
              </Reveal>)}
          </div>
        </div>
      </section>

      <section className="bg-ivory py-24" data-id="element-613" data-mp="1acnf2k1qxz6l">
        <div className="container-page" data-id="element-614" data-mp="1f9nisob4ipuj">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end" data-id="element-615" data-mp="1f7at1oz4jb6x">
            <SectionHeading kicker="Career opportunities" title="Recently added roles." intro="Sample listings for demonstration." data-id="element-616" data-mp="2lp6bdrqq3o9k" />
            <Button to="/jobs" variant="outline" arrow className="self-start md:self-auto" data-id="element-617" data-mp="2o9vz7lojsd6s">
              All opportunities
            </Button>
          </div>
          <ul className="mt-10 grid gap-6 md:grid-cols-3" data-id="element-618" data-mp="2k9v933s36djw">
            {latest.map(j => <li key={j.id} data-id="element-619" data-mp="2y9muhpkxmw7x">
                <JobCard job={j} data-id="element-620" data-mp="2y5p8mw6yzqkc" />
              </li>)}
          </ul>
        </div>
      </section>

      <section id="register" className="scroll-mt-24 bg-white py-24" data-id="element-621" data-mp="378qbmo0jh1js">
        <div className="container-page grid gap-12 lg:grid-cols-12" data-id="element-622" data-mp="376x7pghb8vfh">
          <div className="lg:col-span-4" data-id="element-623" data-mp="3363dy6h6smeo">
            <SectionHeading kicker="Confidential registration" title="Share your profile, discreetly." data-id="element-624" data-mp="335bvzygiin6v" />
            <div className="mt-8 rounded-xl bg-midnight p-6 text-white" data-id="element-625" data-mp="3h0e1wlld0cjo">
              <ShieldCheckIcon className="h-6 w-6 text-gold-300" aria-hidden="true" data-id="element-626" data-mp="3c08drfeat86c" />
              <h3 className="mt-4 font-display text-2xl font-semibold" data-id="element-627" data-mp="3qx8w4salnqs5">Privacy and data handling</h3>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed text-white/80" data-id="element-628" data-mp="3lz7d27d6brgo">
                {dataPoints.map(d => <li key={d} className="flex gap-3" data-id="element-629" data-mp="9c7d1fwvhetk">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" data-id="element-630" data-mp="delwo28d8ilw" />
                    {d}
                  </li>)}
              </ul>
            </div>
          </div>
          <div className="rounded-xl border border-line p-6 md:p-10 lg:col-span-8" data-id="element-631" data-mp="4ddyrl9rhjsc">
            <CandidateRegistrationForm data-id="element-632" data-mp="3lunerctddcyu" />
          </div>
        </div>
      </section>

      <section className="bg-ivory py-24" data-id="element-633" data-mp="3qmfas2vaoawd">
        <div className="container-page grid gap-12 lg:grid-cols-12" data-id="element-634" data-mp="3qo9vbg2nm0zx">
          <div className="lg:col-span-4" data-id="element-635" data-mp="3cw1xrm3jq606">
            <SectionHeading kicker="Candidate FAQs" title="Questions candidates ask us." data-id="element-636" data-mp="3csxqgfusau5a" />
            <div className="mt-10 rounded-xl bg-white p-6" data-id="element-637" data-mp="3hrhbme1k01wl">
              <h3 className="font-display text-2xl font-semibold text-ink" data-id="element-638" data-mp="32xvzdegy7i02">Job alerts</h3>
              <p className="mb-5 mt-1 text-sm text-muted" data-id="element-639" data-mp="37umfg1sr6dlh">Hear about relevant roles first.</p>
              <NewsletterForm purpose="alerts" tone="light" data-id="element-640" data-mp="2orawi57j02il" />
            </div>
          </div>
          <div className="lg:col-span-8" data-id="element-641" data-mp="2k02hmtnffoy1">
            <Accordion items={faqCategories[1].items} defaultOpen={0} data-id="element-642" data-mp="2jz148iixuqd5" />
          </div>
        </div>
      </section>
    </>;
}